import { useId, useMemo, useState } from "react";
import { Braces, Plus, Trash2 } from "lucide-react";
import { Button, Input, NativeSelect, Textarea, cn } from "@ncp/ui";
import type { Condition, FormLocale, I18nText } from "./types";
import { localize } from "./schema";

/** A field the builder can reference: its path, label and value type (drives the operator list and value editor). */
export interface LogicField {
  path: string;
  label: string;
  type?: "string" | "number" | "integer" | "boolean" | "array" | "object" | "date";
  options?: Array<{ value: string; label: string }>;
}

type Op = "==" | "!=" | ">" | ">=" | "<" | "<=" | "in" | "!in" | "isEmpty" | "!isEmpty" | "matches" | "count>" | "count<" | "any" | "daysBetween>";

interface Row {
  kind: "row";
  field: string;
  op: Op;
  value: string;
}

interface Group {
  kind: "group";
  join: "and" | "or";
  items: Node[];
  not?: boolean;
}

type Node = Row | Group;

const OPS: Array<{ op: Op; label: I18nText; types?: LogicField["type"][]; noValue?: boolean; hint?: I18nText }> = [
  { op: "==", label: { en: "equals", ar: "يساوي" } },
  { op: "!=", label: { en: "does not equal", ar: "لا يساوي" } },
  { op: ">", label: { en: "greater than", ar: "أكبر من" }, types: ["number", "integer", "date"] },
  { op: ">=", label: { en: "at least", ar: "على الأقل" }, types: ["number", "integer", "date"] },
  { op: "<", label: { en: "less than", ar: "أقل من" }, types: ["number", "integer", "date"] },
  { op: "<=", label: { en: "at most", ar: "على الأكثر" }, types: ["number", "integer", "date"] },
  { op: "in", label: { en: "is one of", ar: "أحد القيم" }, types: ["string", "number", "integer"] },
  { op: "!in", label: { en: "is not one of", ar: "ليس أحد القيم" }, types: ["string", "number", "integer"] },
  { op: "matches", label: { en: "matches pattern", ar: "يطابق النمط" }, types: ["string"] },
  { op: "isEmpty", label: { en: "is empty", ar: "فارغ" }, noValue: true },
  { op: "!isEmpty", label: { en: "is filled in", ar: "غير فارغ" }, noValue: true },
  { op: "count>", label: { en: "has more than … items", ar: "يحوي أكثر من … عنصر" }, types: ["array"] },
  { op: "count<", label: { en: "has fewer than … items", ar: "يحوي أقل من … عنصر" }, types: ["array"] },
  { op: "any", label: { en: "has an item where …", ar: "يحوي عنصرا حيث …" }, types: ["array"], hint: { en: "field:value", ar: "حقل:قيمة" } },
  { op: "daysBetween>", label: { en: "is more than … days ago", ar: "قبل أكثر من … يوما" }, types: ["date", "string"] },
];

const T = {
  all: { en: "All of the following", ar: "كل ما يلي" },
  any: { en: "Any of the following", ar: "أي مما يلي" },
  addRule: { en: "Add condition", ar: "إضافة شرط" },
  addGroup: { en: "Add group", ar: "إضافة مجموعة" },
  not: { en: "NOT", ar: "ليس" },
  field: { en: "Field…", ar: "حقل…" },
  value: { en: "Value", ar: "القيمة" },
  json: { en: "JSON", ar: "JSON" },
  visual: { en: "Visual", ar: "مرئي" },
  invalid: { en: "Invalid JSON", ar: "JSON غير صالح" },
  always: { en: "Always (no condition)", ar: "دائما (بدون شرط)" },
  remove: { en: "Remove condition", ar: "حذف الشرط" },
  complex: { en: "This expression uses operations the visual builder cannot show. Edit it as JSON.", ar: "يستخدم هذا التعبير عمليات لا يمكن للمنشئ المرئي عرضها. عدله بصيغة JSON." },
  values: { en: "Comma-separated values", ar: "قيم مفصولة بفواصل" },
  refHint: { en: "Type @ to compare against another field (e.g. @transaction.signingDate)", ar: "اكتب @ للمقارنة مع حقل آخر (مثال: @transaction.signingDate)" },
};

// ---------------------------------------------------------------- JsonLogic <-> tree

function toLogic(node: Node, fields: LogicField[]): Condition {
  if (node.kind === "group") {
    const items = node.items.map((n) => toLogic(n, fields)).filter((x) => x !== undefined && x !== true);
    let expr: Condition = items.length === 0 ? true : items.length === 1 ? items[0]! : { [node.join]: items };
    if (node.not) expr = { "!": [expr] };
    return expr;
  }
  const f = fields.find((x) => x.path === node.field);
  const v = coerce(node.value, f);
  const ref = { var: node.field };
  switch (node.op) {
    case "==":
      return { "==": [ref, v] };
    case "!=":
      return { "!=": [ref, v] };
    case ">":
    case ">=":
    case "<":
    case "<=":
      return { [node.op]: [ref, v] };
    case "in":
      return { in: [ref, list(node.value, f)] };
    case "!in":
      return { "!": [{ in: [ref, list(node.value, f)] }] };
    case "matches":
      return { matches: [ref, node.value] };
    case "isEmpty":
      return { isEmpty: [ref] };
    case "!isEmpty":
      return { "!": [{ isEmpty: [ref] }] };
    case "count>":
      return { ">": [{ count: [ref] }, Number(node.value) || 0] };
    case "count<":
      return { "<": [{ count: [ref] }, Number(node.value) || 0] };
    case "any": {
      const [fld, val] = node.value.split(":");
      return { any: [ref, fld ?? "", coerce(val ?? "", undefined)] };
    }
    case "daysBetween>":
      return { ">": [{ daysBetween: [ref] }, Number(node.value) || 0] };
  }
}

/** Values starting with `@` reference another field (`@transaction.signingDate`), so cross-field comparisons stay visual. */
function coerce(raw: string, f?: LogicField): unknown {
  const s = raw.trim();
  if (s.startsWith("@") && s.length > 1) return { var: s.slice(1) };
  if (f?.type === "boolean" || s === "true" || s === "false") return s === "true";
  if ((f?.type === "number" || f?.type === "integer" || (!f && /^-?\d+(\.\d+)?$/.test(s))) && s !== "" && !Number.isNaN(Number(s))) return Number(s);
  return s;
}

function list(raw: string, f?: LogicField): unknown[] {
  return raw.split(",").map((x) => x.trim()).filter(Boolean).map((x) => coerce(x, f));
}

/** Parses a JsonLogic expression back into the tree. Returns null when the shape is not representable. */
function fromLogic(expr: unknown): Node | null {
  if (expr === undefined || expr === null || expr === true) return { kind: "group", join: "and", items: [] };
  if (typeof expr !== "object" || Array.isArray(expr)) return null;
  const [op, argRaw] = Object.entries(expr as Record<string, unknown>)[0] ?? [];
  if (!op) return null;
  const args = Array.isArray(argRaw) ? argRaw : [argRaw];
  if (op === "and" || op === "or") {
    const items = args.map(fromLogic);
    if (items.some((i) => i === null)) return null;
    return { kind: "group", join: op, items: items as Node[] };
  }
  if (op === "!" || op === "!!") {
    const inner = fromLogic(args[0]);
    if (!inner) return null;
    if (inner.kind === "row") {
      if (inner.op === "in") return { ...inner, op: "!in" };
      if (inner.op === "isEmpty") return { ...inner, op: "!isEmpty" };
      if (inner.op === "==") return { ...inner, op: "!=" };
      return { kind: "group", join: "and", items: [inner], not: true };
    }
    return { ...inner, not: !inner.not };
  }
  const varOf = (a: unknown): string | null => (a && typeof a === "object" && "var" in (a as object) ? String((a as { var: unknown }).var) : null);
  const lit = (a: unknown): string => {
    const ref = varOf(a);
    if (ref !== null) return `@${ref}`;
    return Array.isArray(a) ? a.map(String).join(", ") : a === undefined ? "" : String(a);
  };
  if (["==", "!=", ">", ">=", "<", "<="].includes(op)) {
    const left = args[0];
    const right = args[1];
    const v = varOf(left);
    if (v) return { kind: "row", field: v, op: op as Op, value: lit(right) };
    // count / daysBetween wrappers
    if (left && typeof left === "object") {
      const [innerOp, innerArgs] = Object.entries(left as Record<string, unknown>)[0] ?? [];
      const iv = varOf(Array.isArray(innerArgs) ? innerArgs[0] : innerArgs);
      if (innerOp === "count" && iv && (op === ">" || op === "<")) return { kind: "row", field: iv, op: op === ">" ? "count>" : "count<", value: lit(right) };
      if (innerOp === "daysBetween" && iv && op === ">") return { kind: "row", field: iv, op: "daysBetween>", value: lit(right) };
    }
    return null;
  }
  if (op === "in") {
    const v = varOf(args[0]);
    return v ? { kind: "row", field: v, op: "in", value: lit(args[1]) } : null;
  }
  if (op === "isEmpty") {
    const v = varOf(args[0]);
    return v ? { kind: "row", field: v, op: "isEmpty", value: "" } : null;
  }
  if (op === "matches") {
    const v = varOf(args[0]);
    return v ? { kind: "row", field: v, op: "matches", value: lit(args[1]) } : null;
  }
  if (op === "any") {
    const v = varOf(args[0]);
    return v ? { kind: "row", field: v, op: "any", value: `${lit(args[1])}:${lit(args[2])}` } : null;
  }
  return null;
}

// ---------------------------------------------------------------- component

export interface LogicBuilderProps {
  value: Condition | undefined;
  onChange: (value: Condition | undefined) => void;
  fields: LogicField[];
  locale?: FormLocale;
  className?: string;
  /** Show the JSON toggle (default true). */
  allowJson?: boolean;
  /** Placeholder shown when no condition is set. */
  emptyLabel?: I18nText;
}

/** Visual and/or builder for JsonLogic conditions with a JSON escape hatch. Emits `undefined` when there are no rules. */
export function LogicBuilder({ value, onChange, fields, locale = "en", className, allowJson = true, emptyLabel }: LogicBuilderProps) {
  const parsed = useMemo(() => fromLogic(value), [value]);
  const [mode, setMode] = useState<"visual" | "json">(parsed ? "visual" : "json");
  const [jsonText, setJsonText] = useState(() => (value === undefined ? "" : JSON.stringify(value, null, 2)));
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const tree: Group = parsed ? (parsed.kind === "group" ? parsed : { kind: "group", join: "and", items: [parsed] }) : { kind: "group", join: "and", items: [] };

  const emit = (g: Group) => {
    const logic = toLogic(g, fields);
    onChange(logic === true ? undefined : logic);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {allowJson ? (
        <div className="flex items-center justify-end gap-1 text-xs">
          <button type="button" className={cn("rounded px-2 py-0.5", mode === "visual" ? "bg-aeblack-950 text-white" : "hover:bg-sand-100")} disabled={!parsed} onClick={() => setMode("visual")}>{localize(T.visual, locale)}</button>
          <button type="button" className={cn("rounded px-2 py-0.5 inline-flex items-center gap-1", mode === "json" ? "bg-aeblack-950 text-white" : "hover:bg-sand-100")} onClick={() => { setJsonText(value === undefined ? "" : JSON.stringify(value, null, 2)); setMode("json"); }}><Braces className="size-3" /> {localize(T.json, locale)}</button>
        </div>
      ) : null}
      {mode === "json" || !parsed ? (
        <div className="space-y-1">
          {!parsed ? <p className="text-xs text-warning-700">{localize(T.complex, locale)}</p> : null}
          <Textarea
            value={jsonText}
            dir="ltr"
            className="font-mono text-xs"
            rows={6}
            placeholder={'{ "==": [{ "var": "field" }, "value"] }'}
            onChange={(e) => {
              setJsonText(e.target.value);
              const txt = e.target.value.trim();
              if (!txt) {
                setJsonErr(null);
                onChange(undefined);
                return;
              }
              try {
                onChange(JSON.parse(txt) as Condition);
                setJsonErr(null);
              } catch {
                setJsonErr(localize(T.invalid, locale));
              }
            }}
          />
          {jsonErr ? <p className="text-xs text-danger-600">{jsonErr}</p> : null}
        </div>
      ) : (
        <GroupEditor group={tree} onChange={emit} fields={fields} locale={locale} root emptyLabel={emptyLabel} />
      )}
    </div>
  );
}

function GroupEditor({ group, onChange, fields, locale, root, emptyLabel }: { group: Group; onChange: (g: Group) => void; fields: LogicField[]; locale: FormLocale; root?: boolean; emptyLabel?: I18nText }) {
  const setItem = (i: number, n: Node) => onChange({ ...group, items: group.items.map((x, j) => (j === i ? n : x)) });
  const removeItem = (i: number) => onChange({ ...group, items: group.items.filter((_, j) => j !== i) });
  const addRow = () => onChange({ ...group, items: [...group.items, { kind: "row", field: fields[0]?.path ?? "", op: "==", value: "" }] });
  const addGroup = () => onChange({ ...group, items: [...group.items, { kind: "group", join: group.join === "and" ? "or" : "and", items: [{ kind: "row", field: fields[0]?.path ?? "", op: "==", value: "" }] }] });
  return (
    <div className={cn("space-y-2 rounded-xl border p-3", root ? "border-sand-200 bg-white/60" : "border-dashed border-aegold-300 bg-aegold-50/30", group.not && "border-danger-300")}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <NativeSelect value={group.join} onChange={(e) => onChange({ ...group, join: e.target.value as "and" | "or" })} className="h-8 w-auto text-xs">
          <option value="and">{localize(T.all, locale)}</option>
          <option value="or">{localize(T.any, locale)}</option>
        </NativeSelect>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={group.not === true} onChange={(e) => onChange({ ...group, not: e.target.checked || undefined })} /> {localize(T.not, locale)}</label>
      </div>
      {group.items.length === 0 ? <p className="text-xs text-muted-foreground">{localize(emptyLabel ?? T.always, locale)}</p> : null}
      {group.items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            {item.kind === "group" ? <GroupEditor group={item} onChange={(g) => setItem(i, g)} fields={fields} locale={locale} /> : <RowEditor row={item} onChange={(r) => setItem(i, r)} fields={fields} locale={locale} />}
          </div>
          <Button type="button" size="icon-sm" variant="ghost" onClick={() => removeItem(i)} aria-label={localize(T.remove, locale)}><Trash2 className="text-danger-600" /></Button>
        </div>
      ))}
      <div className="flex gap-1">
        <Button type="button" size="sm" variant="ghost" onClick={addRow}><Plus /> {localize(T.addRule, locale)}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={addGroup}><Plus /> {localize(T.addGroup, locale)}</Button>
      </div>
    </div>
  );
}

function RowEditor({ row, onChange, fields, locale }: { row: Row; onChange: (r: Row) => void; fields: LogicField[]; locale: FormLocale }) {
  const listId = useId();
  const f = fields.find((x) => x.path === row.field);
  const ops = OPS.filter((o) => !o.types || !f?.type || o.types.includes(f.type) || (f.type === "string" && o.types.includes("date")));
  const opDef = OPS.find((o) => o.op === row.op);
  const known = fields.some((x) => x.path === row.field);
  return (
    <div className="grid gap-2 sm:grid-cols-[1.3fr_1.2fr_1fr]">
      <NativeSelect value={known ? row.field : "__custom"} onChange={(e) => onChange({ ...row, field: e.target.value === "__custom" ? row.field : e.target.value })} className="h-9 text-xs">
        <option value="" disabled>{localize(T.field, locale)}</option>
        {fields.map((x) => <option key={x.path} value={x.path}>{x.label} <span>({x.path})</span></option>)}
        {!known && row.field ? <option value="__custom">{row.field}</option> : null}
      </NativeSelect>
      <NativeSelect value={row.op} onChange={(e) => onChange({ ...row, op: e.target.value as Op })} className="h-9 text-xs">
        {ops.map((o) => <option key={o.op} value={o.op}>{localize(o.label, locale)}</option>)}
      </NativeSelect>
      {opDef?.noValue ? (
        <span />
      ) : f?.options && (row.op === "==" || row.op === "!=") ? (
        <NativeSelect value={row.value} onChange={(e) => onChange({ ...row, value: e.target.value })} className="h-9 text-xs">
          <option value="">{localize(T.value, locale)}</option>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </NativeSelect>
      ) : f?.type === "boolean" && (row.op === "==" || row.op === "!=") ? (
        <NativeSelect value={row.value || "true"} onChange={(e) => onChange({ ...row, value: e.target.value })} className="h-9 text-xs">
          <option value="true">{locale === "ar" ? "نعم" : "true"}</option>
          <option value="false">{locale === "ar" ? "لا" : "false"}</option>
        </NativeSelect>
      ) : (
        <Input
          value={row.value}
          onChange={(e) => onChange({ ...row, value: e.target.value })}
          className="h-9 text-xs"
          dir="ltr"
          list={row.value.startsWith("@") ? listId : undefined}
          placeholder={row.op === "in" || row.op === "!in" ? localize(T.values, locale) : opDef?.hint ? localize(opDef.hint, locale) : localize(T.value, locale)}
          title={localize(T.refHint, locale)}
          type={f?.type === "date" && !row.value.startsWith("@") && !["in", "!in", "daysBetween>"].includes(row.op) ? "date" : "text"}
        />
      )}
      {row.value.startsWith("@") ? (
        <datalist id={listId}>
          {fields.map((x) => <option key={x.path} value={`@${x.path}`}>{x.label}</option>)}
        </datalist>
      ) : null}
    </div>
  );
}

/** Human-readable sentence for a JsonLogic expression (used in lists and diffs). */
export function describeLogic(expr: Condition | undefined, fields: LogicField[], locale: FormLocale = "en"): string {
  const node = fromLogic(expr);
  if (!node) return JSON.stringify(expr);
  const nameOf = (p: string) => fields.find((f) => f.path === p)?.label ?? p;
  const valueOf = (v: string) => (v.startsWith("@") ? nameOf(v.slice(1)) : v);
  const walk = (n: Node): string => {
    if (n.kind === "row") {
      const op = OPS.find((o) => o.op === n.op);
      const shown = n.op === "any" ? n.value.replace(":", " = ") : valueOf(n.value);
      const label = localize(op?.label ?? n.op, locale).replace("…", shown);
      return `${nameOf(n.field)} ${label}${op?.noValue || label.includes(shown) ? "" : ` ${shown}`}`.trim();
    }
    if (n.items.length === 0) return localize(T.always, locale);
    const inner = n.items.map(walk).map((s, i, arr) => (arr.length > 1 && n.items[i]?.kind === "group" ? `(${s})` : s)).join(n.join === "and" ? (locale === "ar" ? " و " : " and ") : locale === "ar" ? " أو " : " or ");
    return n.not ? `${localize(T.not, locale)} (${inner})` : inner;
  };
  return walk(node);
}
